import { useState } from "react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger } from "@/components/ui/dialog";
import { useApi, money, errText } from "@/lib/api";
import { useCanEdit, VIEW_ONLY_TIP } from "@/components/app/TrialBanner";

const LAGS = [0, 15, 30, 45, 60, 90];
const pct = (v) => (v == null ? "" : String(Math.round(v * 1000) / 10));

export const PipelineDialog = ({ data, onSaved }) => {
  const canEdit = useCanEdit();
  const api = useApi();
  const [open, setOpen] = useState(false);
  const [lag, setLag] = useState(30);
  const [ov, setOv] = useState({});
  const [saving, setSaving] = useState(false);
  const reset = (o) => { setOpen(o); if (o) { setLag(data.settings.lag_days); setOv(Object.fromEntries(data.stages.map((s) => [s.stage, pct(s.override)]))); } };

  const save = async () => {
    const stage_overrides = {};
    for (const [k, v] of Object.entries(ov)) {
      if (v === "") continue;
      const n = Number(v);
      if (!Number.isFinite(n) || n < 0 || n > 100) return toast.error(`Chance for "${k}" must be 0-100%`);
      stage_overrides[k] = n / 100;
    }
    setSaving(true);
    try { onSaved(await api.put("/pipeline/settings", { lag_days: lag, stage_overrides })); toast.success("Pipeline settings saved"); setOpen(false); }
    catch (e) { toast.error(errText(e, "Couldn't save settings")); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={reset}>
      <DialogTrigger asChild>
        <button data-testid="pipeline-review-btn" className="flex-1 rounded-full border border-[#FF7AC6]/50 py-2 text-xs text-[#FF9FD6] hover:bg-[#FF7AC6]/10 transition-colors">Review deals</button>
      </DialogTrigger>
      <DialogContent data-testid="pipeline-dialog" className="max-w-lg bg-[#121212] border-[#2A2A2A] text-white">
        <DialogHeader>
          <DialogTitle>Pipeline settings</DialogTitle>
          <DialogDescription className="text-[#A3A3A3]">Each deal counts as amount × chance, landing when it closes plus the payment delay. Leave a stage blank to use the chance from your sales tool.</DialogDescription>
        </DialogHeader>
        {data.summary.no_chance > 0 && <p data-testid="pipeline-no-chance" className="text-xs text-[#FFD166]">{data.summary.no_chance} {data.summary.no_chance === 1 ? "deal has" : "deals have"} no chance set, so they count as $0. Set a chance for their stage below.</p>}
        <label className="text-xs text-[#A3A3A3] flex items-center justify-between">Payment arrives after close
          <select data-testid="pipeline-lag-select" value={lag} onChange={(e) => setLag(Number(e.target.value))} className="rounded-lg bg-[#050505] border border-[#2A2A2A] px-2 py-1.5 text-sm text-white">
            {[...new Set([...LAGS, lag])].sort((a, b) => a - b).map((l) => <option key={l} value={l}>{l === 0 ? "Same day" : `${l} days`}</option>)}
          </select>
        </label>
        <div className="max-h-64 overflow-y-auto rounded-lg border border-[#2A2A2A] divide-y divide-[#2A2A2A]">
          {data.stages.map((s, i) => (
            <div key={s.stage} data-testid={`pipeline-stage-${i}`} className="flex items-center gap-3 px-3 py-2 text-sm">
              <div className="flex-1 min-w-0"><p className="truncate">{s.stage}</p>
                <p className="font-mono text-[11px] text-[#6B6B6B]">{s.deals} {s.deals === 1 ? "deal" : "deals"} · {money(s.amount, true)} · tool says {s.tool_chance == null ? "—" : `${pct(s.tool_chance)}%`}</p></div>
              <input data-testid={`pipeline-stage-input-${i}`} inputMode="decimal" placeholder={s.tool_chance == null ? "0" : pct(s.tool_chance)} value={ov[s.stage] ?? ""}
                onChange={(e) => setOv((o) => ({ ...o, [s.stage]: e.target.value }))}
                className="w-16 rounded-lg bg-[#050505] border border-[#2A2A2A] px-2 py-1 text-right font-mono text-sm focus:outline-none focus:ring-2 focus:ring-[#FF7AC6]" />
              <span className="text-xs text-[#6B6B6B]">%</span>
            </div>
          ))}
        </div>
        {(data.skipped > 0 || data.closed > 0 || data.summary.beyond_forecast > 0) && (
          <p data-testid="pipeline-import-notes" className="text-[11px] text-[#6B6B6B]">
            {data.closed > 0 && `${data.closed} won/lost deals ignored (they show up in Stripe or QuickBooks). `}
            {data.skipped > 0 && `${data.skipped} rows skipped (missing amount or close date). `}
            {data.summary.beyond_forecast > 0 && `${data.summary.beyond_forecast} deals land beyond the 24-month forecast.`}
          </p>
        )}
        <button data-testid="pipeline-settings-save" onClick={save} disabled={saving || !canEdit} title={canEdit ? undefined : VIEW_ONLY_TIP} className="rounded-full bg-[#FF7AC6] py-2.5 text-sm font-semibold text-[#050505] disabled:opacity-50 disabled:cursor-not-allowed">{saving ? "Saving…" : "Save settings"}</button>
      </DialogContent>
    </Dialog>
  );
};
